import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Upload, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import DigestSettings from "@/components/DigestSettings";

export default function CompanySettingsPage() {
  const [settings, setSettings] = useState({ name: "", address: "", logo_data_url: null });
  const [logoFile, setLogoFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/company-settings");
      setSettings({ name: data.name || "", address: data.address || "", logo_data_url: data.logo_data_url || null });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1_500_000) { toast.error("Logo must be under 1.5MB"); return; }
    setLogoFile(f);
    const r = new FileReader();
    r.onload = () => setPreview(r.result);
    r.readAsDataURL(f);
  };

  const removeLogo = () => { setLogoFile(null); setPreview(null); setSettings({ ...settings, logo_data_url: null, _remove: true }); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", settings.name);
      fd.append("address", settings.address || "");
      if (logoFile) fd.append("logo", logoFile);
      if (settings._remove) fd.append("remove_logo", "true");
      await api.put("/company-settings", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Company settings saved");
      setLogoFile(null); setPreview(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const displayLogo = preview || settings.logo_data_url;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Company Settings</h1>
        <p className="mt-1 text-sm text-zinc-500">This info appears on every payslip you issue.</p>
      </div>
      <form onSubmit={save}>
        <Card className="border-zinc-200 shadow-none">
          <CardHeader><CardTitle className="text-base font-semibold">Brand & identity</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-start gap-6">
              <div className="h-28 w-28 rounded-md border border-dashed border-zinc-300 bg-zinc-50 grid place-items-center overflow-hidden">
                {displayLogo ? (
                  <img src={displayLogo} alt="logo" className="max-h-full max-w-full object-contain" data-testid="logo-preview" />
                ) : (
                  <Building2 className="h-10 w-10 text-zinc-300" strokeWidth={1.4} />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <Label className="text-sm font-medium">Company logo</Label>
                <p className="text-xs text-zinc-500">PNG, JPG or SVG · max 1.5MB · recommend 400×160px</p>
                <div className="flex gap-2">
                  <input ref={inputRef} type="file" accept="image/*" hidden onChange={onPick} data-testid="logo-file-input" />
                  <Button type="button" variant="outline" className="border-zinc-300" onClick={() => inputRef.current?.click()} data-testid="upload-logo-btn">
                    <Upload className="h-4 w-4 mr-2" /> {displayLogo ? "Replace" : "Upload"}
                  </Button>
                  {displayLogo && (
                    <Button type="button" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={removeLogo}>
                      <Trash2 className="h-4 w-4 mr-2" /> Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Company name</Label>
              <Input value={settings.name} onChange={(e) => setSettings({ ...settings, name: e.target.value })} className="mt-1 h-10" required data-testid="company-name-input" />
            </div>
            <div>
              <Label className="text-sm font-medium">Registered address</Label>
              <Textarea rows={3} value={settings.address} onChange={(e) => setSettings({ ...settings, address: e.target.value })} className="mt-1" placeholder="Street, City, State, PIN" data-testid="company-address-input" />
            </div>
          </CardContent>
        </Card>

        <DigestSettings />
        <div className="flex justify-end mt-4">
          <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-company-settings">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>
    </div>
  );
}
